import { flatMap, isNil, isNull, map, reduce } from 'lodash';
import graphqlClient from 'api/graphql';
import titleCase from 'common/utils/titleCase';
import { listCatalogApi } from 'api/drug';
import { SigFieldKeys } from 'enums/SigManagement.enum';
import { SortOrder } from 'enums/common.enum';

import {
  listApprovedDrugsForSigGql,
  listDirectionsForSigGql,
  listSigAssociationsGql,
  upsertSigAssociationsGql,
} from './gql';

// Base sensitivity so casing does not split the list. A raw comparison puts every
// capitalised sig ahead of every lowercase one, which reads as unsorted.
const compareAlphabetically = (readText) => (first, second) =>
  String(readText(first) ?? '').localeCompare(String(readText(second) ?? ''), undefined, {
    sensitivity: 'base',
  });

const compareBySigText = compareAlphabetically(({ sig }) => sig);
const compareByDirectionText = compareAlphabetically(({ direction }) => direction);

// `directionStatus` is the name the upsert input uses for the same flag, so accept
// either and hand the screen one shape.
const toDirection = ({ directionId, direction, isActive, directionStatus }) => ({
  directionId,
  direction,
  isActive: isActive ?? directionStatus ?? false,
});

const toDirectionList = (directions) => map(directions, toDirection).sort(compareByDirectionText);

/**
 * The rows in the order the screen should show them.
 *
 * Sorted by sig here as well as on the server when that is the sort key, case-insensitively. The
 * guarantee then belongs to the shape the screen renders, so a change to the server's ordering
 * cannot quietly ship an unsorted list.
 *
 * When the user has picked another column the server's order IS the answer, and this leaves it
 * alone. Re-sorting by sig regardless is what it used to do, and it silently defeated the date
 * columns the moment they became sortable: the request went out with the right sort key, the server
 * answered in date order, and this put it straight back into alphabetical order — so the header's
 * arrow moved and the rows did not.
 */
export const orderSigRowsForDisplay = (sigRows, sortBy) => {
  const rows = [...(sigRows ?? [])];

  // Sig is the default, so an absent sortBy still gets the alphabetical guarantee.
  if ((sortBy?.sortKey ?? SigFieldKeys.SIG) !== SigFieldKeys.SIG) {
    return rows;
  }

  const sortedRows = rows.sort(compareBySigText);

  return sortBy?.sortOrder === SortOrder.DESCENDING ? sortedRows.reverse() : sortedRows;
};

export const listSigAssociationsApi = ({ page, sortBy, filter: sigFilter }) => {
  const defaultItemsPerPage = Number(process.env.REACT_APP_DEFAULT_ITEMS_PER_PAGE);
  const defaultPageOffset = Number(process.env.REACT_APP_DEFAULT_PAGE_OFFSET);

  return graphqlClient
    .request(listSigAssociationsGql, {
      limit: defaultItemsPerPage,
      offset: (page - 1) * defaultItemsPerPage || defaultPageOffset,
      sortBy,
      filter: reduce(
        sigFilter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(({ listSigAssociations: { count, result } }) => ({
      count,
      result: orderSigRowsForDisplay(result, sortBy),
    }));
};

// Largest page the server will serve whatever is asked for, so the loop below advances by this and
// cannot spin.
const DIRECTION_PAGE_SIZE = 200;

// Both halves of the detail view. Separate calls, because the directions are on the sig database
// and the drug approvals on the catalog one — a single query cannot reach both.
export const listDirectionsForSigApi = ({ sigId, limit, offset }) => {
  return graphqlClient
    .request(listDirectionsForSigGql, { sigId, limit, offset })
    .then(({ listDirectionsForSig: { count, result } }) => ({
      count,
      result: toDirectionList(result),
    }));
};

/**
 * Every direction on the sig, following the pages until they run out.
 *
 * Not an optimisation — a correctness requirement. `upsertSigAssociations` retires any existing
 * direction that is absent from the list it is given, so saving a sig with only the first page
 * loaded would retire the rest without saying so. Both writers (the edit form and the status flip)
 * have to hold the complete list before they submit.
 */
export const listAllDirectionsForSigApi = ({ sigId }) => {
  const fetchFrom = (offset, collected) =>
    listDirectionsForSigApi({ sigId, limit: DIRECTION_PAGE_SIZE, offset }).then(
      ({ count, result }) => {
        const directions = [...collected, ...result];

        // Guarded on an empty page as well as the count, so a count that disagrees with the rows
        // cannot turn this into an endless walk.
        if (directions.length >= count || result.length === 0) {
          return { count, result: directions.sort(compareByDirectionText) };
        }

        return fetchFrom(offset + DIRECTION_PAGE_SIZE, directions);
      }
    );

  return fetchFrom(0, []);
};

export const listApprovedDrugsForSigApi = ({ sigId, limit, offset, includeRetired }) => {
  return graphqlClient
    .request(listApprovedDrugsForSigGql, { sigId, limit, offset, includeRetired })
    .then(({ listApprovedDrugsForSig: { count, result } }) => ({
      count,
      // Cased at the boundary so no raw API string reaches a component, and ordered by the name the
      // screen actually shows rather than the one the server sorted.
      //
      // The name only. withTitleCasedDrugFields would take `strength` with it, and a unit's casing
      // carries meaning a pharmacist reads — "2.4 mg/0.75 mL" coming back as "2.4 Mg/0.75 Ml" is
      // wrong, not merely differently cased.
      result: map(result, (drug) => ({ ...drug, drugName: titleCase(drug.drugName) })).sort(
        compareAlphabetically(({ drugName }) => drugName)
      ),
    }));
};

/**
 * Catalog items to offer when attaching one to a sig.
 *
 * `listCatalog` returns a drug with its variants nested, but an approval is held against one
 * variant's `catalogId` — a strength and pack size, not a drug name. So the variants are flattened
 * into one pickable row each, carrying the packaging the picker has to show for the choice to mean
 * anything.
 */
export const listCatalogItemsForSigApi = ({ drugName }) => {
  return listCatalogApi({ drugName, isActive: true }).then(({ results }) =>
    flatMap(results, ({ drugName: catalogDrugName, variants }) =>
      map(variants, ({ catalogId, strength, dosageForm, packSizeValue, packSizeUnit }) => ({
        catalogId,
        // Cased here, not at render: the same name reaching two screens two ways is the thing the
        // api boundary exists to prevent. The strength is left alone — a unit read back as "Mg"
        // instead of "mg" is wrong rather than untidy.
        drugName: titleCase(catalogDrugName),
        strength,
        form: dosageForm ?? undefined,
        packSizeValue: isNil(packSizeValue) ? undefined : String(packSizeValue),
        packSizeUnit: packSizeUnit ?? undefined,
      }))
    ).sort(compareAlphabetically(({ drugName: name, strength }) => `${name} ${strength ?? ''}`))
  );
};

export const upsertSigAssociationsApi = ({ input }) => {
  return graphqlClient
    .request(upsertSigAssociationsGql, { input })
    .then(({ upsertSigAssociations }) => ({
      ...upsertSigAssociations,
      // Retiring or restoring a sig moves its drug approvals, and the screen has never shown
      // them. The name is cased here so no raw API string reaches a component; the strength is left
      // alone, because a unit read as "Mg" instead of "mg" is wrong rather than untidy.
      affectedDrugApprovals: map(upsertSigAssociations?.affectedDrugApprovals ?? [], (drug) => ({
        ...drug,
        drugName: titleCase(drug.drugName),
      })),
    }));
};
